import type { ErrorRespuesta, RespuestaLogin } from '@barbones/shared';

/** En producción la API vive en el mismo dominio (Caddy enruta /api). */
export const API_URL: string = (import.meta.env.VITE_API_URL as string | undefined) ?? '';

export class ErrorApi extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ErrorApi';
  }
}

interface EstadoSesion {
  token: string | null;
  usuarioId?: string | null;
  sucursalId: string | null;
  onRenovado?: (respuesta: RespuestaLogin) => void;
  onExpirada?: () => void;
}

const estado: EstadoSesion = { token: null, sucursalId: null };

export function configurarSesion(cambios: Partial<EstadoSesion>): void {
  Object.assign(estado, cambios);
}

export const tokenActual = () => estado.token;
export const usuarioActualId = () => estado.usuarioId ?? null;

type ResultadoRenovacion = { tipo: 'ok'; datos: RespuestaLogin } | { tipo: 'sin_sesion' } | { tipo: 'error' };

let renovacionEnCurso: Promise<ResultadoRenovacion> | null = null;

/** Un intento de renovar el access token con la cookie de refresh (una sola petición en vuelo). */
function intentarRenovar(): Promise<ResultadoRenovacion> {
  renovacionEnCurso ??= fetch(`${API_URL}/api/auth/refresh`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: '{}',
  })
    .then(async (res): Promise<ResultadoRenovacion> => {
      if (res.status === 401 || res.status === 403) return { tipo: 'sin_sesion' };
      if (!res.ok) return { tipo: 'error' };
      const datos = (await res.json()) as RespuestaLogin;
      estado.token = datos.accessToken;
      estado.onRenovado?.(datos);
      return { tipo: 'ok', datos };
    })
    .catch((): ResultadoRenovacion => ({ tipo: 'error' }))
    .finally(() => {
      renovacionEnCurso = null;
    });
  return renovacionEnCurso;
}

/** Renueva la sesión; devuelve null si no hay sesión o no se pudo contactar al servidor. */
export async function renovarSesion(): Promise<RespuestaLogin | null> {
  const r = await intentarRenovar();
  return r.tipo === 'ok' ? r.datos : null;
}

/**
 * Restaura la sesión al abrir la app. Solo un 401 significa "sin sesión"; si el servidor
 * no responde (reinicio, red), se reintenta con espera creciente antes de rendirse.
 */
export async function restaurarSesion(intentos = 5, esperaMs = 800): Promise<RespuestaLogin | null> {
  for (let i = 0; i < intentos; i++) {
    const r = await intentarRenovar();
    if (r.tipo === 'ok') return r.datos;
    if (r.tipo === 'sin_sesion') return null;
    await new Promise((ok) => setTimeout(ok, esperaMs * 2 ** i));
  }
  return null;
}

export interface OpcionesApi {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined | null>;
  /** Clave de idempotencia; si no se da, las mutaciones generan una automáticamente. */
  idempotencyKey?: string;
  signal?: AbortSignal;
  /** Envío desde la cola sin conexión: si vuelve a fallar la red no se encola otra vez. */
  desdeCola?: boolean;
}

/** Qué hacer con una mutación cuando no hay red (lo registra `offline/cola.ts`). */
export interface ManejadorSinRed {
  clasificar(ruta: string, method: string): 'encolable' | 'requiere_conexion' | 'normal';
  encolar(m: { id: string; ruta: string; method: 'POST' | 'PUT' | 'PATCH' | 'DELETE'; body?: unknown; propietario?: string }): Promise<void>;
}
let sinRed: ManejadorSinRed | null = null;
export function usarManejadorSinRed(m: ManejadorSinRed): void {
  sinRed = m;
}

function construirUrl(ruta: string, query?: OpcionesApi['query']): string {
  const url = new URL(`${API_URL}/api${ruta}`, window.location.origin);
  for (const [clave, valor] of Object.entries(query ?? {})) {
    if (valor !== undefined && valor !== null && valor !== '') url.searchParams.set(clave, String(valor));
  }
  return url.toString();
}

async function enviar(ruta: string, opciones: OpcionesApi, idempotencyKey?: string): Promise<Response> {
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (opciones.body !== undefined && !(opciones.body instanceof FormData)) headers['Content-Type'] = 'application/json';
  if (estado.token) headers.Authorization = `Bearer ${estado.token}`;
  if (estado.sucursalId) headers['X-Sucursal-Id'] = estado.sucursalId;
  if (idempotencyKey) headers['Idempotency-Key'] = idempotencyKey;
  return fetch(construirUrl(ruta, opciones.query), {
    method: opciones.method ?? 'GET',
    headers,
    credentials: 'include',
    signal: opciones.signal,
    body:
      opciones.body === undefined
        ? undefined
        : opciones.body instanceof FormData
          ? opciones.body
          : JSON.stringify(opciones.body),
  });
}

async function leerError(res: Response): Promise<ErrorApi> {
  try {
    const cuerpo = (await res.json()) as ErrorRespuesta;
    return new ErrorApi(res.status, String(cuerpo.error.code), cuerpo.error.message, cuerpo.error.details);
  } catch {
    return new ErrorApi(res.status, 'INTERNO', 'No se pudo completar la operación');
  }
}

/** Llamada tipada a la API con renovación automática de sesión. */
export async function api<T>(ruta: string, opciones: OpcionesApi = {}): Promise<T> {
  const mutacion = (opciones.method ?? 'GET') !== 'GET';
  const clave = mutacion ? (opciones.idempotencyKey ?? crypto.randomUUID()) : undefined;

  let res: Response;
  try {
    res = await enviar(ruta, opciones, clave);
  } catch (error) {
    if (!(error instanceof TypeError) || !mutacion || opciones.desdeCola || !sinRed || opciones.body instanceof FormData) throw error;
    const tipo = sinRed.clasificar(ruta, opciones.method!);
    if (tipo === 'requiere_conexion') throw new ErrorApi(503, 'REQUIERE_CONEXION', 'Esta operación necesita conexión a internet. Inténtalo cuando vuelva la red.');
    if (tipo === 'normal') throw error;
    await sinRed.encolar({ id: clave!, ruta, method: opciones.method as 'POST', body: opciones.body, propietario: estado.usuarioId ?? undefined });
    throw new ErrorApi(202, 'ENCOLADA', 'Sin conexión: el cambio se guardó y se enviará en cuanto vuelva la red.');
  }
  if (res.status === 401 && !ruta.startsWith('/auth/')) {
    const r = await intentarRenovar();
    if (r.tipo === 'sin_sesion') {
      estado.onExpirada?.();
      throw await leerError(res);
    }
    // Sin conexión con el servidor: se conserva la sesión y se informa el error.
    if (r.tipo === 'error') throw new ErrorApi(503, 'SIN_CONEXION', 'Sin conexión con el servidor');
    res = await enviar(ruta, opciones, clave);
  }
  if (!res.ok) throw await leerError(res);
  if (res.status === 204) return undefined as T;
  const tipo = res.headers.get('content-type') ?? '';
  return (tipo.includes('application/json') ? await res.json() : await res.blob()) as T;
}

/** Descarga un archivo (Excel/PDF) respetando la sesión. */
export async function descargar(ruta: string, query: OpcionesApi['query'], nombre: string): Promise<void> {
  const blob = await api<Blob>(ruta, { query });
  const url = URL.createObjectURL(blob);
  const a = Object.assign(document.createElement('a'), { href: url, download: nombre });
  a.click();
  URL.revokeObjectURL(url);
}

/** El cambio quedó en la cola sin conexión (no es un error real para el usuario). */
export const esEncolada = (error: unknown) => error instanceof ErrorApi && error.code === 'ENCOLADA';

export function mensajeError(error: unknown): string {
  if (error instanceof ErrorApi) return error.message;
  if (error instanceof TypeError) return 'Sin conexión con el servidor';
  return 'Ocurrió un error inesperado';
}
